import { useContext, useState } from 'react'
import { useAuth } from 'react-oidc-context'
import { Box, Button, Card, CardHeader, CircularProgress, Divider, Grid, List, ListItemButton, ListItemText, Typography, useTheme } from '@mui/material'
import { useSnackbar } from 'notistack'

import { AppContext } from '../../../../providers/app-provider'
import Api from '../../../../utils/api'
import { convertStringToArray } from '../../../../utils/helpers'
import useSharedStyles from '../../../../utils/shared.styles'
import usePeopleStyles from '../../index.styles'

import useStyles from './index.styles'

const not = (a: ISubscriptionListItem[], b: ISubscriptionListItem[]) => a.filter((value) => b.find((item) => item.id === value.id) === undefined)

const intersection = (a: ISubscriptionListItem[], b: ISubscriptionListItem[]) => a.filter((value) => b.find((item) => item.id === value.id) !== undefined)

const sortAlphabetical = (list: ISubscriptionListItem[]) => list.sort((a, b) => a.name.localeCompare(b.name))

const TransferList = ({
  user,
  availableList,
  activeList,
  onSave,
  onCancel,
}: {
  user: IUserType
  availableList: ISubscriptionListItem[]
  activeList: ISubscriptionListItem[]
  onSave: (newList: ISubscriptionListItem[]) => Promise<void>
  onCancel: () => void
}) => {
  const classes = useStyles
  const sharedClasses = useSharedStyles
  const peopleClasses = usePeopleStyles
  const theme = useTheme()
  const { tree, host } = useContext(AppContext)
  const auth = useAuth()
  const { enqueueSnackbar } = useSnackbar()

  const [isSaving, setIsSaving] = useState<boolean>(false)
  const [checked, setChecked] = useState<ISubscriptionListItem[]>([])
  const [previousSelected, setPreviousSelected] = useState<ISubscriptionListItem>()
  const [left, setLeft] = useState<ISubscriptionListItem[]>(not(availableList, activeList))
  const [right, setRight] = useState<ISubscriptionListItem[]>(activeList)
  const [loadingProgramSubscriptions, setLoadingProgramSubscriptions] = useState<boolean>(false)

  const leftChecked = intersection(checked, left)
  const rightChecked = intersection(checked, right)

  const handleToggle = (e: React.MouseEvent<HTMLDivElement, MouseEvent>, value: ISubscriptionListItem, items: ISubscriptionListItem[]) => {
    const currentIndex = checked.indexOf(value)
    const newChecked = [...checked]
    const isShiftKey = e.shiftKey
    const isCtrlKey = e.metaKey || e.ctrlKey

    if (isShiftKey) {
      const previousIndex = previousSelected ? items.indexOf(previousSelected) : -1
      // multiple select if there is already one selected
      if (currentIndex === -1) {
        // new selection
        newChecked.push(value)
        setPreviousSelected(value)
        // everything in between
        if (previousIndex > -1) {
          const min = Math.min(previousIndex, items.indexOf(value))
          const max = Math.max(previousIndex, items.indexOf(value))
          const inBetween = items.slice(min, max)
          newChecked.push(...inBetween)
        }
      } else {
        // remove selection
        newChecked.splice(currentIndex)
        setPreviousSelected(newChecked[newChecked.length - 1])
      }
      setChecked(sortAlphabetical(newChecked.filter((item, index) => newChecked.indexOf(item) === index))) // remove duplicates
    } else if (isCtrlKey) {
      // single select - don't replace currently selected
      if (currentIndex === -1) {
        newChecked.push(value)
        setPreviousSelected(value)
      } else {
        newChecked.splice(currentIndex, 1)
      }
      setChecked(sortAlphabetical(newChecked))
    } else if ((!isCtrlKey && !isShiftKey && currentIndex === -1) || (!isCtrlKey && !isShiftKey && currentIndex > -1 && checked.length > 1)) {
      // single select - replace currently selected
      setChecked([value])
      setPreviousSelected(value)
    } else {
      // single unselect - replace currently selected
      setChecked([])
    }
  }

  const numberOfChecked = (items: ISubscriptionListItem[]) => intersection(checked, items).length

  const handleAllRight = () => {
    setRight(sortAlphabetical(right.concat(left)))
    setLeft([])
  }

  const handleCheckedRight = () => {
    setRight(sortAlphabetical(right.concat(leftChecked)))
    setLeft(not(left, leftChecked))
    setChecked(not(checked, leftChecked))
  }

  const handleCheckedLeft = () => {
    setLeft(sortAlphabetical(left.concat(rightChecked)))
    setRight(not(right, rightChecked))
    setChecked(not(checked, rightChecked))
  }

  const handleAllLeft = () => {
    setLeft(sortAlphabetical(left.concat(right)))
    setRight([])
  }

  const selectProgramSubscriptions = async () => {
    if (!host || !auth.user?.access_token) return
    if (tree) {
      setLoadingProgramSubscriptions(true)
      const defaultSchemas: string[] = []

      // Scenario 1: User is assigned to 1+ Client Company folders but needs to be subscribed to schemas on descendant Program folders
      // Get all descendants of the folders the user is assigned to, then get the themes of the Programs and extract the defaultSchemas
      const childrenApiRequests = user.folders.map((folderId) => Api.get(`${host}/api/folders/children/${folderId}`))
      await Promise.all(childrenApiRequests).then(async (responses) => {
        const failures = responses.filter((response) => !response.ok).length
        if (failures > 0) {
          enqueueSnackbar('Could not select program subscriptions.', { variant: 'error' })
        } else {
          const children = responses.map((response) => response.body.children).flat()
          // Get the programs
          const resPrograms = await Api.post(`${host}/api/folders/query?detail=true`, {
            filters: [
              { fieldName: 'id', operator: 'in', value: children },
              { fieldName: 'type', operator: 'eq', value: 'Program' },
            ],
            first: 10000,
            type: 'and',
          })
          if (resPrograms.ok) {
            resPrograms.body.results.forEach((program: IFolderType) => {
              if (program.theme?.defaultSchemas) {
                try {
                  let schemasToAdd = program.theme.defaultSchemas
                  if (typeof schemasToAdd === 'string') schemasToAdd = convertStringToArray(schemasToAdd)
                  defaultSchemas.push(...(schemasToAdd as string[]))
                } catch {
                  enqueueSnackbar(`There was a problem selecting schemas from ${program.name}. Please check the formatting.`, { variant: 'error' })
                }
              }
            })
          } else {
            enqueueSnackbar('Could not select program subscriptions.', { variant: 'error' })
          }
        }
      })

      // Scenario 2: User is assigned to 1+ Participant folders but needs to be subscribed to schemas on ancestor Program folders
      // Get the combined theme for each of the assigned folders and add to defaultSchemas list
      const themeApiRequests = user.folders.map((folderId) => Api.get(`${host}/api/folders/${folderId}/theme`))
      await Promise.all(themeApiRequests)
        .then((responses) => {
          const failures = responses.filter((response) => !response.ok).length
          if (failures > 0) {
            enqueueSnackbar('Could not select program subscriptions.', { variant: 'error' })
          } else {
            // Get all defaultSchemas from the themes
            responses.forEach((res) => {
              if (res.body.theme?.defaultSchemas) {
                try {
                  let schemasToAdd = res.body.theme.defaultSchemas
                  if (typeof schemasToAdd === 'string') schemasToAdd = convertStringToArray(schemasToAdd)
                  defaultSchemas.push(...(schemasToAdd as string[]))
                } catch {
                  enqueueSnackbar(`There was a problem selecting schemas from one of the themes. Please check the formatting.`, { variant: 'error' })
                }
              }
            })
            // Remove duplicates
            let uniqueSchemas = [...new Set(defaultSchemas)]
            // Remove any that are already on the right
            uniqueSchemas = uniqueSchemas.filter((schemaName) => !right.find((s) => s.name === schemaName))
            // Select any from available list that exist
            setChecked(uniqueSchemas.map((schemaName) => availableList.find((s) => s.name === schemaName)).filter((s) => s) as ISubscriptionListItem[])

            enqueueSnackbar('Program subscriptions selected successfully.')
          }
        })
        .finally(() => {
          setLoadingProgramSubscriptions(false)
        })
    }
  }

  const customList = (title: React.ReactNode, items: ISubscriptionListItem[]) => (
    <Card sx={classes.listCard}>
      <CardHeader
        sx={{ px: 2, py: 1 }}
        title={title}
        subheader={`${numberOfChecked(items)}/${items.length} selected`}
        slotProps={{
          title: { variant: 'subtitle2' },
          subheader: { variant: 'body2' },
        }}
      />
      <Divider />
      <List sx={classes.list} dense component="div" role="list">
        {items.map((value: ISubscriptionListItem) => {
          const labelId = `transfer-list-all-item-${value.id}-label`

          return (
            <ListItemButton
              key={value.id}
              role="listitem"
              onClick={(e) => {
                handleToggle(e, value, items)
              }}
              sx={{ backgroundColor: checked.indexOf(value) !== -1 ? 'primary.shades.8p' : 'background.paper', padding: '12px 16px' }}
            >
              <ListItemText
                id={labelId}
                primary={value.name}
                slotProps={{
                  primary: { variant: 'body1' },
                }}
              />
            </ListItemButton>
          )
        })}
      </List>
    </Card>
  )

  return (
    <>
      <Grid container sx={[sharedClasses.centerContent, { gap: 1 }]}>
        <Grid size={5.5}>{customList('Subscription List', left)}</Grid>
        <Grid>
          <Grid container sx={[sharedClasses.centerContentVertical, { flexDirection: 'column' }]}>
            <Button sx={{ my: 0.5 }} variant="outlined" size="small" onClick={handleAllRight} disabled={left.length === 0} aria-label="move all right">
              ≫
            </Button>
            <Button
              sx={{ my: 0.5 }}
              variant="outlined"
              size="small"
              onClick={handleCheckedRight}
              disabled={leftChecked.length === 0}
              aria-label="move selected right"
            >
              &gt;
            </Button>
            <Button
              sx={{ my: 0.5 }}
              variant="outlined"
              size="small"
              onClick={handleCheckedLeft}
              disabled={rightChecked.length === 0}
              aria-label="move selected left"
            >
              &lt;
            </Button>
            <Button sx={{ my: 0.5 }} variant="outlined" size="small" onClick={handleAllLeft} disabled={right.length === 0} aria-label="move all left">
              ≪
            </Button>
          </Grid>
        </Grid>
        <Grid size={5.5}>{customList('Active Subscriptions', right)}</Grid>
      </Grid>
      <Box
        sx={{
          padding: '24px 16px 8px 4px',
        }}
      >
        <Button variant="outlined" onClick={selectProgramSubscriptions} disabled={!tree || loadingProgramSubscriptions}>
          Select All Program Subscriptions
          {loadingProgramSubscriptions && <CircularProgress size={24} sx={{ marginLeft: 2 }} />}
        </Button>
      </Box>
      <Typography
        variant="body1"
        sx={{
          padding: '8px 4px 0 4px',
        }}
      >
        Hold down <span style={classes.keyboardChip(theme)}>shift</span> and click on items to multi-select consecutive items
      </Typography>
      <Typography
        variant="body1"
        sx={{
          padding: '8px 4px 0 4px',
        }}
      >
        Hold down <span style={classes.keyboardChip(theme)}>ctrl</span> or <span style={classes.keyboardChip(theme)}>cmd</span> and click to multi-select
        non-consecutive items
      </Typography>
      <Box sx={peopleClasses.bottomButtonsContainer}>
        <Button
          variant="contained"
          disabled={isSaving}
          onClick={async () => {
            setIsSaving(true)
            await onSave(right)
            setIsSaving(false)
          }}
          sx={peopleClasses.rightSpaceButton}
        >
          Save
        </Button>
        <Button onClick={onCancel}>Cancel</Button>
      </Box>
    </>
  )
}

export default TransferList
